import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";

export interface SiteEstimatorStats {
  quotaLimit: number;
  totalReportsUsed: number;
  totalReportsRemaining: number;
}

export interface FleetEstimatorStats {
  quotaLimit: number;
  totalReportsUsed: number;
  totalReportsRemaining: number;
}

export interface AccountStatisticsResponse {
  statistics: {
    siteEstimator: SiteEstimatorStats;
    fleetEstimator: FleetEstimatorStats;
    planDuration: string;
    planExpiresIn: number | null;
  };
}

export interface AccountStatisticsApiResponse {
  success: boolean;
  message: string;
  data: AccountStatisticsResponse;
}

export interface RevenueCostDataset {
  label: string;
  data: number[];
  _id?: string;
}

export interface LastReportDetailsResponse {
  reportName: string;
  generatedOn: string;
  netProfit: number;
  totalEstimatedCost: number;
  estimatedPaybackPeriods: number;
  revenueCost: {
    labels: string[];
    datasets: RevenueCostDataset[];
  };
}

export interface LastReportDetailsApiResponse {
  success: boolean;
  message: string;
  data?: {
    report: LastReportDetailsResponse;
  };
}

export interface MetricsResponse {
  totalActiveUsers: number;
  totalActiveSubscriptionUserByTier: {
    starter: number;
    professional: number;
    team: number;
    enterprise: number;
  };
  churnRate: string;
  totalNewSignUpUser: number;
}

export interface MetricsApiResponse {
  success: boolean;
  message: string;
  data?: MetricsResponse;
}

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

// GET USER ACCOUNT STATISTICS ACTION - UPDATED
export const getUserAccountStatistics = createAsyncThunk<
  AccountStatisticsApiResponse,
  void,
  { rejectValue: string }
>("dashboard/getUserAccountStatistics", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<AccountStatisticsApiResponse>(
      "/subscription/statistics"
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(
        response.data.message || "Failed to retrieve account statistics"
      );
      return rejectWithValue(
        response.data.message || "Failed to retrieve account statistics"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as any).message ||
        "Failed to retrieve account statistics";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET LAST REPORT DETAILS ACTION
export const getLastReportDetails = createAsyncThunk<
  LastReportDetailsApiResponse,
  void,
  { rejectValue: string }
>("dashboard/getLastReportDetails", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<LastReportDetailsApiResponse>(
      "/report/last"
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(
        response.data.message || "Failed to retrieve last report details"
      );
      return rejectWithValue(
        response.data.message || "Failed to retrieve last report details"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as LastReportDetailsApiResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET USER AND SUBSCRIPTION METRICS ACTION
export const getUserAndSubscriptionMetrics = createAsyncThunk<
  MetricsApiResponse,
  void,
  { rejectValue: string }
>("dashboard/getUserAndSubscriptionMetrics", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<MetricsApiResponse>(
      "/users/metrics"
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(
        response.data.message ||
          "Failed to retrieve user and subscription metrics"
      );
      return rejectWithValue(
        response.data.message ||
          "Failed to retrieve user and subscription metrics"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as MetricsApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});
